import { useEffect, useRef, useState } from "react";
import { Download, CheckCircle2, X, Loader2 } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/App";
import {
  downloadSurahForOffline,
  isSurahDownloaded,
  removeSurahDownload,
} from "@/lib/offlineDownloader";

/**
 * Per-surah download control. Three states:
 *   • idle     — gold download icon
 *   • downloading — spinner + progress %
 *   • done     — green check (click again to remove)
 *
 * Stops event propagation so the parent surah card's onClick (open Reader)
 * isn't triggered when this button is used.
 */
export default function OfflineDownloadButton({
  surahNumber,
  reciter,
  className = "",
  size = "sm", // "sm" | "md"
}) {
  const [downloaded, setDownloaded] = useState(() =>
    isSurahDownloaded(surahNumber, reciter)
  );
  const [downloading, setDownloading] = useState(false);
  const [progress, setProgress] = useState({ done: 0, total: 0 });
  const abortRef = useRef(false);

  useEffect(() => {
    setDownloaded(isSurahDownloaded(surahNumber, reciter));
  }, [surahNumber, reciter]);

  const sizeClass = size === "md" ? "w-9 h-9" : "w-7 h-7";
  const iconSize = size === "md" ? "w-4 h-4" : "w-3.5 h-3.5";

  const handleDownload = async (e) => {
    e.stopPropagation();
    e.preventDefault();
    if (downloading) {
      // Cancel
      abortRef.current = true;
      return;
    }
    if (downloaded) {
      // Remove
      removeSurahDownload(surahNumber, reciter);
      setDownloaded(false);
      toast.info(`Surah ${surahNumber} removed from offline downloads`);
      return;
    }
    abortRef.current = false;
    setDownloading(true);
    setProgress({ done: 0, total: 0 });
    try {
      await downloadSurahForOffline({
        surahNumber,
        reciter,
        apiBase: `${API}`,
        onProgress: (done, total) => setProgress({ done, total }),
        shouldAbort: () => abortRef.current,
      });
      setDownloaded(true);
      toast.success(`Surah ${surahNumber} ready for offline use!`);
    } catch (err) {
      if (err?.message !== "aborted") {
        toast.error(`Download failed: ${err?.message || "network error"}`);
      } else {
        toast.info("Download cancelled");
      }
    } finally {
      setDownloading(false);
      setProgress({ done: 0, total: 0 });
      abortRef.current = false;
    }
  };

  // ---- Rendered states ----
  if (downloading) {
    const pct =
      progress.total > 0
        ? Math.round((progress.done / progress.total) * 100)
        : 0;
    return (
      <button
        data-testid={`offline-download-cancel-${surahNumber}`}
        onClick={handleDownload}
        className={`${className} group flex items-center gap-1.5 px-2.5 ${sizeClass.split(" ")[1]} rounded-full border border-[#C9A227]/40 bg-[#0D2818]/80 hover:bg-[#1B4D3E] transition-colors`}
        title={`Downloading… ${pct}%  (click to cancel)`}
        aria-label={`Cancel download (${pct}%)`}
      >
        <Loader2 className={`${iconSize} text-[#C9A227] animate-spin`} />
        <span className="text-[10px] font-outfit text-[#E8D5A3] tabular-nums">
          {pct}%
        </span>
        <X className="w-3 h-3 text-[#E8D5A3]/50 group-hover:text-[#E8D5A3]" />
      </button>
    );
  }

  if (downloaded) {
    return (
      <button
        data-testid={`offline-downloaded-${surahNumber}`}
        onClick={handleDownload}
        className={`${className} ${sizeClass} flex items-center justify-center rounded-full bg-emerald-600/20 border border-emerald-500/40 hover:bg-emerald-600/35 transition-colors`}
        title="Saved offline · click to remove"
        aria-label="Remove offline download"
      >
        <CheckCircle2 className={`${iconSize} text-emerald-400`} />
      </button>
    );
  }

  return (
    <button
      data-testid={`offline-download-${surahNumber}`}
      onClick={handleDownload}
      className={`${className} ${sizeClass} flex items-center justify-center rounded-full border border-[#C9A227]/40 hover:border-[#C9A227] hover:bg-[#C9A227]/15 transition-colors`}
      title="Download for offline use"
      aria-label="Download for offline use"
    >
      <Download className={`${iconSize} text-[#C9A227]`} />
    </button>
  );
}
